import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {notifyOnboardingProgress} from '../../utils/onboardingProgress';

/**
 * Las llamadas del wizard «Nueva línea de control», todas en un lugar.
 *
 * Devuelven el objeto crudo del backend: `APIClient` nunca rechaza, así que quien llama siempre
 * recibe un objeto y decide con `status !== 'OK'`. La interpretación vive en los módulos puros
 * (`materializeLine`, `accountsList`, `campaignsPreview`), que se testean sin red.
 */

export function fetchAgencyData() {
    return new APIClient(`${API_URL}get-agency-data`).call('GET', {}, {cache: true});
}

export function fetchClientBasicData(clientId) {
    return new APIClient(`${API_URL}client/${clientId}/basic-data`).call('GET');
}

export function fetchClient(clientId) {
    return new APIClient(`${API_URL}client/${clientId}`).call('GET');
}

export function fetchConnections() {
    return new APIClient(`${API_URL}agency/connections/`).call('GET');
}

export function fetchChannelAccounts(channel) {
    return new APIClient(`${API_URL}get-accounts/${channel}`).call('GET');
}

export function postClient(name) {
    return new APIClient(`${API_URL}client/add`).call('POST', {name});
}

export function postBusinessUnit(payload) {
    // Crear una línea puede completar pasos de «Tus primeros pasos»: el drawer se tilda solo.
    return new APIClient(`${API_URL}business-unit/add`).call('POST', payload).then(notifyOnboardingProgress);
}

export function putGoal(businessUnitId, priority, payload) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/goals/${priority}`).call('PUT', payload);
}

export function fetchBusinessUnitAllAdAccounts(businessUnitId) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/all_ad_accounts`).call('GET');
}

export function postCampaignsConfigurations(businessUnitId, adAccountIds) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/campaigns-configurations/add`)
        .call('POST', adAccountIds).then(notifyOnboardingProgress);
}

export function fetchCampaignsConfigurations(businessUnitId) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/campaigns-configurations`).call('GET');
}

export function fetchBusinessUnitBudget(businessUnitId) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/budget`).call('GET');
}

export function fetchCampaigns(adAccountId, configurationId) {
    return new APIClient(`${API_URL}get-campaigns/${adAccountId}/${configurationId}`).call('GET');
}

// Las campañas de la cuenta que NO están corriendo, según la base (lo que usa el «Sólo activos» del
// clásico): `{all_not_active_campaigns: [{id, name}]}`.
export function fetchInactiveCampaigns(adAccountId) {
    return new APIClient(`${API_URL}agency/ad-accounts/${adAccountId}/campaigns/inactive`).call('GET');
}

export function fetchAnalyticsAccounts(businessUnitId) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/all_ad_accounts/analytics`).call('GET');
}

export function fetchAdAccountActions(adAccountId) {
    return new APIClient(`${API_URL}agency/ad-accounts/${adAccountId}/actions`).call('GET');
}

// Los conteos 1D / 7D / 30D de los eventos de una cuenta de GA4 (`{action_id: counts}`, caché de 24 h
// en el backend). Los otros canales ya los traen en `/actions`.
export function fetchAdAccountActionCounts(adAccountId) {
    return new APIClient(`${API_URL}agency/ad-accounts/${adAccountId}/actions/counts`).call('GET');
}

// La vista sólo implementa `put()`: un POST responde 405. Acá se eligen los eventos de conversión,
// que junto con el presupuesto completan «Tablero útil».
export function putCampaignsConfigurationsEdit(businessUnitId, payload) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/campaigns-configurations/edit`)
        .call('PUT', payload).then(notifyOnboardingProgress);
}

// Los días de corrida, por el endpoint parcial: no re-envía el presupuesto ni los objetivos.
export function putScheduleConfiguration(businessUnitId, days) {
    return new APIClient(`${API_URL}business-unit/${businessUnitId}/modify-period-dates/`)
        .call('PUT', {period_dates_data: days});
}

export function fetchAuthUrlFor(channelPath) {
    return new APIClient(`${API_URL}get_auth_url/${channelPath}`).call('GET');
}
